import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Shipments from './pages/Shipments';
import CreateShipment from './pages/CreateShipment';
import PackageItems from './pages/PackageItems';
import Tracking from './pages/Tracking';
import Labels from './pages/Labels';
import Profile from './pages/Profile';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminShipments from './pages/admin/AdminShipments';
import AdminBilling from './pages/admin/AdminBilling';
import AdminUsers from './pages/admin/AdminUsers';
import AdminInvites from './pages/admin/AdminInvites';
import AdminSettings from './pages/admin/AdminSettings';
import './App.css';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

// Public Route component (redirect to appropriate dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (user) {
    // Redirect based on user type
    if (user.is_admin) {
      return <Navigate to="/g-admin" />;
    } else {
      return <Navigate to="/dashboard" />;
    }
  }
  
  return children;
};

// Admin Route component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (!user.is_admin) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

// Root redirect component
const RootRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (user) {
    // Redirect based on user type
    if (user.is_admin) {
      return <Navigate to="/g-admin" />;
    } else {
      return <Navigate to="/dashboard" />;
    }
  }
  
  return <Navigate to="/login" />;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } 
            />
            <Route 
              path="/register" 
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/shipments" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <Shipments />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/create-shipment" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <CreateShipment />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/package-items" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <PackageItems />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/tracking" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <Tracking />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/labels" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <Labels />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <Profile />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            
            {/* Admin Routes */}
            <Route 
              path="/g-admin" 
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminDashboard />
                  </AdminLayout>
                </AdminRoute>
              } 
            />
            <Route 
              path="/g-admin/shipments" 
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminShipments />
                  </AdminLayout>
                </AdminRoute>
              } 
            />
            <Route 
              path="/g-admin/billing" 
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminBilling />
                  </AdminLayout>
                </AdminRoute>
              } 
            />
            <Route 
              path="/g-admin/users" 
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminUsers />
                  </AdminLayout>
                </AdminRoute>
              } 
            />
            <Route 
              path="/g-admin/invites" 
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminInvites />
                  </AdminLayout>
                </AdminRoute>
              } 
            />
            <Route 
              path="/g-admin/settings" 
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminSettings />
                  </AdminLayout>
                </AdminRoute>
              } 
            />
            
            <Route path="/" element={<RootRedirect />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
